// @flow
/** @jsx jsx */

import { jsx } from '@emotion/core';
import * as React from 'react';
import Button from 'react-bootstrap/lib/Button';
import Modal from 'react-bootstrap/lib/Modal';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';
import ControlLabel from 'react-bootstrap/lib/ControlLabel';
import FormControl from 'react-bootstrap/lib/FormControl';
import { Query } from 'react-apollo';
import styled from '@emotion/styled';

import type { CommonActionProps } from './types.actions';
import type { GridItem } from '../../types';
import ToggleController from '../../controllers/ToggleController';
import ButtonAction from './ButtonAction';
import LabelCell from '../LabelCell';
import FormRow from '../FormRow';
import DescriptionCell from '../DescriptionCell';
import inventoryReviewsQuery from '../../graphql/queries/inventoryReviews.query';
import Loading from '../Loading';
import { DesktopView, MobileView } from '../../config/mediaQuery';
import Row from '../Row';

type Props = {
  item: GridItem,
};

const Col = styled('div')`
  flex: 1;
`;

const Spacer = styled('div')`
  width: 20px;
`;

const TextAreaDetails = (props: { field: string, title: string }) => (
  <Col>
    <FormRow
      css={{
        flexDirection: 'column',
      }}
    >
      <Col>
        <ControlLabel>{props.title}</ControlLabel>
      </Col>
      <DescriptionCell>
        <FormControl
          componentClass="textarea"
          value={props.field || ''}
          readOnly
          css={{
            minHeight: '100px',
          }}
        />
      </DescriptionCell>
    </FormRow>
  </Col>
);

const InputDetails = (props: { field?: string, title: string }) => (
  <>
    <DesktopView>
      <FormRow>
        <LabelCell>
          <ControlLabel>{props.title}</ControlLabel>
        </LabelCell>
        <DescriptionCell>
          <FormControl type="text" value={props.field || ''} readOnly />
        </DescriptionCell>
      </FormRow>
    </DesktopView>
    <MobileView>
      <div className='input-label' style={{ width: '100%', textAlign: 'left' }}>
        {props.title}
      </div>
      <FormControl type="text" value={props.field || ''} readOnly />
    </MobileView>
  </>
);

const InventoryNotesAction = (props: { item: GridItem }) => {
  const { item } = props;

  if (!item) return null;

  // Only send Code to match InventoryRowInput and avoid GraphQL 400 errors.
  const selectedRow = { Code: item.Code };

  return (
    <Query
      query={inventoryReviewsQuery}
      variables={{
        selectedRow,
        moduleName: 'inventoryReviews',
      }}
    >
      {({ data, loading, error }: any) => {
        if (loading) {
          return <Loading />;
        }

        if (error) {
          return <div style={{ padding: '20px', color: '#888' }}>Unable to load reviews. Please try again.</div>;
        }

        const {
          ScoreWA,
          ScoreWS,
          ScoreVM,
          ScoreOther,
        } = item;

        const reviews = (data && data.inventoryReviews) || {};
        const ReviewWA = reviews.ReviewWA || '';
        const ReviewWS = reviews.ReviewWS || '';
        const ReviewVM = reviews.ReviewVM || '';
        const ReviewOther = reviews.ReviewOther || '';

        return (
          <MobileView>
            {(isMobile) => (

              <div>
                <DesktopView>
                  {!isMobile &&
                    <FormRow>
                      <ControlLabel>
                        {item.Code} {item.Description}
                      </ControlLabel>
                    </FormRow>
                  }
                  <FormRow>
                    <Col
                      css={{
                        flexDirection: 'column',
                        flex: 1,
                      }}
                    >
                      <InputDetails field={ScoreWA} title="Wine Advocate (WA)" />
                      <InputDetails field={ScoreWS} title="Wine Spectator (WS)" />
                    </Col>
                    <Spacer />
                    <Col
                      css={{
                        flexDirection: 'column',
                        flex: 1,
                      }}
                    >
                      <InputDetails field={ScoreVM} title="Vinous Media (VM)" />
                      <InputDetails field={ScoreOther} title="Other Press" />
                    </Col>
                  </FormRow>

                  <FormRow>
                    <TextAreaDetails
                      field={ReviewWA}
                      title="Wine Advocate (WA) Review"
                    />
                    <Spacer />
                    <TextAreaDetails
                      field={ReviewWS}
                      title="Wine Spectator (WS) Review"
                    />
                  </FormRow>
                  <FormRow>
                    <TextAreaDetails
                      field={ReviewVM}
                      title="Vinous Media (VM)  Review"
                    />
                    <Spacer />
                    <TextAreaDetails
                      field={ReviewOther}
                      title="Other Press Reviews"
                    />
                  </FormRow>
                </DesktopView>
                <MobileView>
                  <Row>
                    <Col
                      css={{
                        flex: 1,
                      }}
                    >
                      <InputDetails field={ScoreWA} title="Wine Advocate (WA)" />
                      <InputDetails field={ScoreWS} title="Wine Spectator (WS)" />
                    </Col>
                    <Spacer />
                    <Col
                      css={{
                        flex: 1,
                      }}
                    >
                      <InputDetails field={ScoreVM} title="Vinous Media (VM)" />
                      <InputDetails field={ScoreOther} title="Other Press" />
                    </Col>
                  </Row>
                  <TextAreaDetails
                    field={ReviewWA}
                    title="Wine Advocate (WA) Review"
                  />
                  <TextAreaDetails
                    field={ReviewVM}
                    title="Vinous Media (VM)  Review"
                  />
                  <TextAreaDetails
                    field={ReviewWS}
                    title="Wine Spectator (WS) Review"
                  />
                  <TextAreaDetails
                    field={ReviewOther}
                    title="Other Press Reviews"
                  />
                </MobileView>
              </div>
            )}
          </MobileView>
        );
      }}
    </Query>
  );
};

const AdditionalInfoAction = (props: CommonActionProps & Props) => (
  <ToggleController>
    {({ onToggleVisibility, visible }) => (
      <React.Fragment>
        <DesktopView>
          <ButtonAction
            bsSize={props.size}
            disabled={props.disabled}
            onClick={onToggleVisibility}
          >
            <Glyphicon glyph="th" /> Ratings / Reviews
          </ButtonAction>
        </DesktopView>
        <MobileView>
          <button disabled={props.disabled} onClick={onToggleVisibility}>
            <img src='assets/rating.svg' alt='rating icon'/> Ratings / Reviews
          </button>
        </MobileView>
        <MobileView>
          {(isMobile) => (
            <Modal show={visible} onHide={onToggleVisibility} dialogClassName={isMobile ? 'inventory-ratings-modal' : ''}>
              <Modal.Header closeButton={!isMobile}>
                <Modal.Title>Inventory Ratings / Review</Modal.Title>
                {isMobile && props.item && <p>{`${props.item.Code} ${props.item.Description}`}</p>}
              </Modal.Header>
              <Modal.Body>
                <InventoryNotesAction item={props.item} />
              </Modal.Body>
              <Modal.Footer>
                <DesktopView>
                  <Button onClick={onToggleVisibility}>Close</Button>
                </DesktopView>
                <MobileView>
                  <div className='button-single-modal'>
                    <button
                      onClick={onToggleVisibility}
                    >
                      Close
                    </button>
                  </div>
                </MobileView>
              </Modal.Footer>
            </Modal>
          )}
        </MobileView>
      </React.Fragment>
    )}
  </ToggleController>
);

AdditionalInfoAction.defaultProps = {
  size: 'xsmall',
};

export default AdditionalInfoAction;
